import {
  assoc,
  defaultTo,
  dissoc,
  dissocPath,
  fromPairs,
  inc,
  lensPath,
  lensProp,
  merge,
  over,
  pipe,
  prop,
  unless,
} from 'ramda';
import map from 'lodash/fp/map';
import groupBy from 'lodash/fp/groupBy';
import { combineReducers } from 'redux';
import { addItem, dropItem, mapEntries } from '../../common/helpers/functional';
import {
  ADD_BARBER,
  ADD_DISCOUNT,
  ADD_ITEM,
  IS_EMPTY,
  POPULATE,
  REMOVE_BARBER,
  REMOVE_DISCOUNT,
  REMOVE_ITEM,
  RESET,
  SET_ITEM_QUANTITY,
  SET_MEMBERSHIP,
  SET_PRESERVE_ORDER_CUSTOMER,
} from './constants';

const barbers = (state = [], { type, payload }) => {
  switch (type) {
    case RESET:
      return [];
    case ADD_BARBER:
      return addItem(payload, state);
    case REMOVE_BARBER:
      return dropItem(payload, state);
    case ADD_ITEM:
      return addItem(payload.barberId, state);
    case POPULATE:
      return pipe(
        groupBy('barberId'),
        Object.keys,
      )(payload.items);
    default:
      return state;
  }
};

const itemId = (itemType, id) => `${itemType}-${id}`;
const getItemId = item => itemId(item.type, item.id);
const barberItems = (state = {}, { type, payload }) => {
  const addBarberItem = () => pipe(
    unless(
      prop(payload.barberId),
      assoc(payload.barberId, {})
    ),
    over(
      lensProp(payload.barberId),
      addItem(itemId(payload.item.type, payload.item.id)),
    )
  )(state);

  const removeBarberItem = () => over(
      lensProp(payload.barberId),
      dropItem(payload.id),
      state,
    );

  switch (type) {
    case RESET:
      return {};
    case ADD_BARBER:
      return assoc(payload, [], state);
    case REMOVE_BARBER:
      return dissoc(payload, state);
    case ADD_ITEM:
      return addBarberItem();
    case REMOVE_ITEM:
      return removeBarberItem();
    case POPULATE:
      return pipe(
        groupBy('barberId'),
        mapEntries((key, value) => ([
          key,
          map(getItemId)(value),
        ])),
        fromPairs,
      )(payload.items);
    default:
      return state;
  }
};

const barberItemsById = (state = {}, { type, payload }) => {
  const increase = pipe(defaultTo(0), inc);
  const increaseQuantity = over(lensProp('quantity'), increase);
  const addOneItem = () => over(
      lensPath([payload.barberId, itemId(payload.item.type, payload.item.id)]),
      pipe(
        merge(payload.item),
        assoc('barberId', payload.barberId),
        increaseQuantity
      ),
      state
    );
  const setItemQuantity = () => over(
    lensPath([payload.barberId, itemId(payload.item.type, payload.item.id)]),
    pipe(
      merge(payload.item),
      assoc('barberId', payload.barberId),
      assoc('quantity', payload.quantity)
    ),
    state
  );

  switch (type) {
    case RESET:
      return {};
    case REMOVE_BARBER:
      return dissoc(payload, state);
    case ADD_ITEM:
      return addOneItem(payload.item.type);
    case REMOVE_ITEM:
      return dissocPath([payload.barberId, payload.id], state);
    case SET_ITEM_QUANTITY:
      return setItemQuantity();
    case POPULATE:
      return pipe(
        groupBy('barberId'),
        mapEntries((key, value) => ([
          key,
          pipe(
            groupBy(getItemId),
            mapEntries((itemKey, itemValue) => ([
              itemKey,
              itemValue[0],
            ])),
            fromPairs,
          )(value),
        ])),
        fromPairs,
      )(payload.items);
    default:
      return state;
  }
};

const discounts = (state = [], { type, payload }) => {
  switch (type) {
    case RESET:
    case IS_EMPTY:
      return [];
    case ADD_DISCOUNT:
      return addItem({ id: payload }, state);
    case REMOVE_DISCOUNT:
      return dropItem({ id: payload }, state);
    case POPULATE:
      return payload.discounts;
    default:
      return state;
  }
};

const membership = (state = null, { type, payload }) => {
  switch (type) {
    case RESET:
      return null;
    case SET_MEMBERSHIP:
      return payload;
    case POPULATE:
      return payload.membership;
    default:
      return state;
  }
};

const preserveOrderCustomer = (state = false, { type, payload }) => {
  switch (type) {
    case RESET:
      return false;
    case SET_PRESERVE_ORDER_CUSTOMER:
      return payload;
    default:
      return state;
  }
};

const order = combineReducers({
  barbers,
  barberItems,
  barberItemsById,
  discounts,
  membership,
  preserveOrderCustomer,
});

export default order;
